import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type FormEvent } from 'react'
import {
  removeCatalogImage,
  saveCatalogEntry,
  serviceCatalogFormQuery,
  uploadCatalogImage,
} from '../api/serviceCatalog'
import { toast } from '../ui/notify'
import { SelectPicker } from '../ui/SelectPicker'
import { SettingsCheck } from '../ui/SettingsParts'
import { Spinner } from '../ui/Spinner'
import './ServiceCatalogForm.css'

/**
 * Formularul unei intrari - `Service_catalog::form()` si macheta
 * `views/version-3/inventoree/service_catalog/form.php`.
 *
 * FEREASTRA E INGUSTA, nu `modal-lg`, si asta e o alegere: lata, campurile
 * incapeau pe doua-trei coloane, si exact aia o facea greu de citit - ochiul
 * sarea intre coloane in loc sa coboare pe o singura linie.
 *
 * APROBAREA NU SE MAI ARATA. In macheta veche cele doua campuri stau sub un
 * `if (false)`: aprobarea se face acum in fluxul tipului de tichet, ca stadiu.
 * Valorile calatoresc mai departe ascunse, ca o salvare de descriere sa nu le
 * stearga - la fel ca in portal.
 *
 * ICONITELE sunt aceeasi lista de treizeci ca in macheta. Cea pusa acum ramane
 * o alegere valabila chiar daca a iesit intre timp din lista, si "No icon" e
 * chiar prima: fara ea nu s-ar putea scoate una pusa din greseala.
 */

/** `$iconChoices` din `form.php`, in aceeasi ordine. */
const ICONS: [string, string][] = [
  ['fa-university', 'Bank'],
  ['fa-money-bill', 'Money'],
  ['fa-credit-card', 'Card'],
  ['fa-piggy-bank', 'Savings'],
  ['fa-hand-holding-usd', 'Lending'],
  ['fa-file-invoice', 'Invoice'],
  ['fa-exchange-alt', 'Transfers'],
  ['fa-store', 'Branch'],
  ['fa-id-card', 'Identity'],
  ['fa-user-shield', 'Compliance'],
  ['fa-key', 'Access'],
  ['fa-lock', 'Security'],
  ['fa-shield-alt', 'Protection'],
  ['fa-database', 'Database'],
  ['fa-server', 'Server'],
  ['fa-network-wired', 'Network'],
  ['fa-cloud', 'Cloud'],
  ['fa-plug', 'Integration'],
  ['fa-cogs', 'Middleware'],
  ['fa-desktop', 'Workstation'],
  ['fa-mobile-alt', 'Mobile'],
  ['fa-globe', 'Web'],
  ['fa-envelope', 'Mail'],
  ['fa-print', 'Printing'],
  ['fa-file-alt', 'Documents'],
  ['fa-chart-line', 'Reporting'],
  ['fa-life-ring', 'Support'],
  ['fa-headset', 'Service desk'],
  ['fa-tools', 'Maintenance'],
  ['fa-clipboard-check', 'Audit'],
]

export function ServiceCatalogForm({ id, onSaved, onClose }: {
  id: number
  onSaved(): void
  onClose(): void
}) {
  const client = useQueryClient()
  const form = useQuery(serviceCatalogFormQuery(id))

  const [ciId, setCiId] = useState('')
  const [shortDescription, setShortDescription] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [longDescription, setLongDescription] = useState('')
  const [icon, setIcon] = useState('')
  const [templateId, setTemplateId] = useState('')
  const [published, setPublished] = useState(false)
  const [scope, setScope] = useState('1')
  const [ticketType, setTicketType] = useState('')
  const [children, setChildren] = useState<number[]>([])
  const [roleIds, setRoleIds] = useState<number[]>([])

  /* Campurile se umplu ODATA, cand raspunsul soseste - nu la fiecare desenare,
     care ar fi rescris peste ce tocmai a tastat omul. */
  const entry = form.data?.entry ?? null
  useEffect(() => {
    if (!form.data) return
    setCiId(entry ? String(entry.ci_id) : '')
    setShortDescription(entry?.short_description ?? '')
    setCategoryId(entry && entry.category_id > 0 ? String(entry.category_id) : '')
    setLongDescription(entry?.long_description ?? '')
    setIcon(entry?.icon ?? '')
    setTemplateId(entry && entry.commercial_template_id > 0 ? String(entry.commercial_template_id) : '')
    setPublished(!!entry?.published)
    setScope(String(entry?.publish_scope ?? 1))
    setTicketType(entry && entry.request_ticket_type_id > 0 ? String(entry.request_ticket_type_id) : '')
    setChildren(form.data.childTicketTypeIds)
    setRoleIds(entry?.role_ids ?? [])
  }, [form.data, entry])

  /* Imaginea se urca pe loc, separat de "Save": e un fisier, nu un camp, si
     intrarea trebuie sa existe deja (are nevoie de id). */
  const image = useMutation({
    mutationFn: (file: File | null) => (file === null ? removeCatalogImage(id) : uploadCatalogImage(id, file)),
    onSuccess: (out) => {
      toast(out.message, out.status === 'OK' ? 'Saved' : 'Error', out.status === 'OK' ? 'success' : 'error')
      if (out.status === 'OK') void client.invalidateQueries({ queryKey: ['service-catalog'] })
    },
    onError: (error: Error) => toast(error.message, 'Error', 'error'),
  })

  const save = useMutation({
    mutationFn: () =>
      saveCatalogEntry({
        ID: id,
        ci_id: ciId,
        short_description: shortDescription,
        category_id: categoryId,
        long_description: longDescription,
        icon,
        commercial_template_id: templateId,
        published: published ? 1 : 0,
        publish_scope: scope,
        request_ticket_type_id: ticketType,
        /* Aprobarea calatoreste asa cum a venit: campurile ei nu se mai arata,
           dar o salvare de descriere n-are voie sa le stearga. */
        requires_approval: entry?.requires_approval ?? 0,
        approver_group_id: entry?.approver_group_id ?? 0,
        'child_ticket_type_ids[]': children,
        /* Rolurile pleaca mereu impreuna cu semnul ca au fost trimise: o
           lista goala nu pleaca deloc din formular, si fara semn serverul n-ar
           sti ca omul a scos toate bifele. */
        ...(form.data?.rolesAvailable ? { 'role_ids[]': roleIds, roles_sent: 1 } : {}),
      }),
    onSuccess: (out) => {
      toast(out.message, out.status === 'OK' ? 'Saved' : 'Error', out.status === 'OK' ? 'success' : 'error')
      if (out.status === 'OK') {
        void client.invalidateQueries({ queryKey: ['service-catalog'] })
        onSaved()
      }
    },
  })

  if (!form.data) {
    return (
      <div className="modal-body text-center">
        <Spinner />
      </div>
    )
  }

  const page = form.data
  const known = ICONS.some(([name]) => name === icon)

  const submit = (e: FormEvent) => {
    e.preventDefault()
    save.mutate()
  }

  return (
    <form id="catalogForm" onSubmit={submit}>
      <div className="modal-body">
        {page.isEdit && entry ? (
          <p className="text-muted">
            Describing <strong>{entry.name}</strong> ({entry.kind === 4 ? 'business service' : 'IT service'}). The
            service itself is edited in CMDB.
          </p>
        ) : (
          <div className="form-group">
            <label htmlFor="catService">Service</label>
            {/*
              Lista cu cautare, ca in macheta (`selectpicker`
              `data-live-search`). Un `<select>` gol o facea o lista de sute de
              servicii prin care se coboara cu rotita.
            */}
            <SelectPicker
              id="catService"
              className="form-control"
              title="Choose one"
              name="ci_id"
              value={ciId}
              onChange={setCiId}
              options={page.services.map((one) => ({
                value: String(one.ID),
                label: `${one.name} · ${one.kind === 4 ? 'business' : 'IT'}`
                  + (one.company_name ? ` · ${one.company_name}` : ''),
              }))}
            />
            <span className="text-muted small">
              Only IT services and business services appear here. Anything else is an item the service rests on, not a
              service.
              {page.services.length === 0 && (
                <>
                  <br />
                  <strong>There are none left to add</strong> — create one in CMDB with a service class first.
                </>
              )}
            </span>
          </div>
        )}

        <div className="row">
          <div className="col-md-12 form-group">
            <label htmlFor="catShort">One line about it</label>
            <input
              type="text"
              className="form-control"
              id="catShort"
              maxLength={500}
              placeholder="What it does, for somebody who does not work in IT"
              value={shortDescription}
              onChange={(e) => setShortDescription(e.target.value)}
            />
          </div>

          <div className="col-md-12 form-group">
            <label htmlFor="catCategory">Category</label>
            <select
              className="form-control"
              id="catCategory"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
            >
              <option value="">None</option>
              {page.categories.map((one) => (
                <option value={one.ID} key={one.ID}>
                  {one.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="catLong">The full description</label>
          <textarea
            className="form-control"
            id="catLong"
            rows={4}
            placeholder="What it covers, what it does not, who it is for"
            value={longDescription}
            onChange={(e) => setLongDescription(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label htmlFor="catImage">Image</label>
          {/*
            Imaginea serviciului (cerinta CEC 33) - pe cardul din magazin si din
            widgetul de intranet. Merge in bucketul public al serverului de
            fisiere; se poate pune numai pe o intrare salvata.
          */}
          <div className="cat-image">
            {entry?.image_url ? (
              <img className="cat-image-preview" src={entry.image_url} alt="" />
            ) : (
              <span className="cat-image-empty text-muted">
                <i className={`fas ${icon || 'fa-image'}`}></i>
              </span>
            )}
            <div>
              {!page.isEdit ? (
                <span className="text-muted small">Save the entry first, then add its image.</span>
              ) : !page.imagesAvailable ? (
                <span className="text-muted small">The file server is not configured, so images cannot be stored.</span>
              ) : (
                <>
                  <input
                    type="file"
                    id="catImage"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    disabled={!page.canWrite || image.isPending}
                    onChange={(e) => {
                      const file = e.target.files?.[0]
                      if (file) image.mutate(file)
                      e.target.value = ''
                    }}
                  />
                  {entry?.image_url && page.canWrite && (
                    <button
                      type="button"
                      className="btn btn-default btn-xs"
                      disabled={image.isPending}
                      onClick={() => image.mutate(null)}
                    >
                      <i className="fas fa-trash"></i> Remove image
                    </button>
                  )}
                  <span className="text-muted small d-block">JPG, PNG, WebP or GIF, up to 2 MB. Shown instead of the icon.</span>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-12 form-group">
            <label htmlFor="catIcon">Icon</label>
            {/*
              Iconita se VEDE in lista, ca in macheta, unde fiecare optiune
              poarta `data-content="<i class='fas ...'></i> Eticheta"`. Fara
              poza, un selector de iconite e un sir de treizeci de cuvinte.
              "No icon" e prima si e o alegere, nu absenta uneia: fara ea nu
              s-ar putea scoate una pusa din greseala.
            */}
            <SelectPicker
              id="catIcon"
              className="form-control"
              title="No icon"
              name="icon"
              size={12}
              value={icon}
              onChange={setIcon}
              options={[
                { value: '', label: 'No icon', icon: 'fa-ban' },
                ...(!known && icon !== '' ? [{ value: icon, label: 'Current', icon }] : []),
                ...ICONS.map(([name, label]) => ({ value: name, label, icon: name })),
              ]}
            />
            <span className="text-muted small">
              Shown on the card in the catalogue, where the eye finds it before the name.
            </span>
          </div>

          <div className="col-md-12 form-group">
            <label htmlFor="catTemplate">
              Contract
              {/*
                Cine deschide fisa si nu gaseste contractul potrivit trebuie
                sa-l faca, iar ecranul care il face e in alt modul, la trei
                clicuri prin meniu. Fila noua, ca sa nu piarda ce a completat
                aici. Legatura o da SERVERUL, cu dreptul lui - un rand care
                duce la 403 e mai rau decat lipsa lui.
              */}
              {page.canManageContracts && (
                <a
                  href="/app/settings/customersV2/contracts"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted small"
                  style={{ fontWeight: 400, marginLeft: 6 }}
                >
                  <i className="fas fa-external-link-alt"></i> manage contracts
                </a>
              )}
            </label>
            <select
              className="form-control"
              id="catTemplate"
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
            >
              <option value="">None</option>
              {page.templates.map((one) => (
                <option value={one.ID} key={one.ID}>
                  {one.name}
                  {one.estimate_unit_cost !== null ? ` · ${one.estimate_unit_cost.toFixed(2)} ${one.currency}` : ''}
                </option>
              ))}
            </select>
            <span className="text-muted small">
              Already carries the SLA, the coverage and a fixed cost. Link one and that fixed cost is used; the cost
              rules add only what varies on top.
            </span>
          </div>
        </div>

        <hr />

        <div className="adm-policy">
          <SettingsCheck
            id="catPublish"
            checked={published}
            onChange={setPublished}
            label={
              <>
                <strong>Published.</strong> An unpublished entry is a draft: it exists here and nowhere else.
              </>
            }
          />
        </div>

        <div className="form-group cat-sub">
          <label htmlFor="catScope">Visible to</label>
          <select className="form-control" id="catScope" value={scope} onChange={(e) => setScope(e.target.value)}>
            {page.scopes.map((one) => (
              <option value={one.value} key={one.value}>
                {one.label}
              </option>
            ))}
          </select>
          <span className="text-muted small">
            Infrastructure stays internal. The person asking for a laptop has no use for &ldquo;core banking
            database&rdquo;.
          </span>
        </div>

        <div className="form-group cat-sub">
          <label htmlFor="catRoles">Roles that see it</label>
          {page.rolesAvailable ? (
            <SelectPicker
              id="catRoles"
              className="form-control"
              title="Every role"
              name="role_ids[]"
              multiple
              size={10}
              selectedTextFormat="count > 2"
              value={roleIds.map(String)}
              onChange={(next) => setRoleIds(next.map(Number))}
              options={page.roles.map((one) => ({ value: String(one.id), label: one.name }))}
            />
          ) : (
            <p className="text-muted small">Not available yet - the roles migration has not run on this server.</p>
          )}
          <span className="text-muted small">
            Leave empty and everybody who can see the catalogue sees it, as today. Pick roles and only people with one of
            them see it in the shop, the intranet widget and the API - and only they can request it.
          </span>
        </div>

        <div className="form-group cat-sub">
          <label htmlFor="catTicketType">Parent ticket type</label>
          <select
            className="form-control"
            id="catTicketType"
            value={ticketType}
            onChange={(e) => setTicketType(e.target.value)}
          >
            <option value="">Nothing — it cannot be requested</option>
            {page.ticketTypes.map((one) => (
              <option value={one.ID} key={one.ID}>
                {one.type_name}
              </option>
            ))}
          </select>
          <span className="text-muted small">
            The ticket a request opens; the child tickets below hang under it. Left empty, the service never reaches the
            catalogue — a card that opens nothing is worse than one that is not there.
          </span>
        </div>

        <div className="form-group cat-sub">
          <label htmlFor="catChildTypes">Child ticket types</label>
          {/*
            Alegere multipla cu bife si cu cautare, ca in macheta
            (`selectpicker multiple data-live-search
            data-selected-text-format="count > 2"`). Un `<select multiple>` gol
            cere ctrl-clic ca sa alegi al doilea pas - si pierde primul cand
            cineva nu stie asta.
          */}
          <SelectPicker
            id="catChildTypes"
            className="form-control"
            title="None — the request opens a single ticket"
            name="child_ticket_type_ids[]"
            multiple
            size={10}
            selectedTextFormat="count > 2"
            value={children.map(String)}
            onChange={(next) => setChildren(next.map(Number))}
            options={page.ticketTypes.map((one) => ({ value: String(one.ID), label: one.type_name }))}
          />
          <span className="text-muted small">
            One extra ticket opens for each type picked here, hanging under the parent — each in its own workflow, so
            with its own team. This is how one service carries all its steps, without a catalogue entry per step.
          </span>
        </div>
      </div>

      <div className="modal-footer">
        <button type="button" className="btn btn-default" onClick={onClose}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={save.isPending}>
          <i className="fas fa-check"></i> Save
        </button>
      </div>
    </form>
  )
}
